import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { X } from 'lucide-react';
import ImageCropper from './ImageCropper';
import MyTeamsCard from '../athlete/MyTeamsCard';
import TeamJoinRequestCard from '../athlete/TeamJoinRequestCard';
import ConfirmDialog from '../ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ProfileModal - A modal component for viewing and editing user profile
 * Opens from the header avatar dropdown
 */
const ProfileModal = ({ open, onOpenChange, user, onUpdate }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [loading, setLoading] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Change password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  if (!open) return null;

  const handleEdit = () => {
    // Parse existing height back into feet and inches
    let height_feet = '';
    let height_inches = '';
    if (user?.height) {
      const heightMatch = user.height.match(/(\d+)'(\d+)"/);
      if (heightMatch) {
        height_feet = heightMatch[1];
        height_inches = heightMatch[2];
      }
    }
    
    // Parse weight to remove "lbs" suffix for editing
    let weight = user?.weight || '';
    if (weight && weight.includes('lbs')) {
      weight = weight.replace(' lbs', '').trim();
    }
    
    setEditingData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      age: user?.age || '',
      height_feet: height_feet,
      height_inches: height_inches,
      weight: weight,
      primary_position: user?.primary_position || '',
      graduation_year: user?.graduation_year || '',
      share_contact_info: user?.share_contact_info ?? false,
      experience_level: user?.experience_level || '',
      training_frequency: user?.training_frequency || '',
      key_strengths: user?.key_strengths || [],
      areas_to_improve: user?.areas_to_improve || []
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Process data types and combine height fields before sending
      const updateData = { ...editingData };
      
      // Convert age to integer if provided, or remove if empty
      if (updateData.age && updateData.age.toString().trim()) {
        updateData.age = parseInt(updateData.age, 10);
      } else {
        delete updateData.age;
      }
      
      // Convert number_of_players to integer if provided, or remove if empty
      if (updateData.number_of_players) {
        updateData.number_of_players = parseInt(updateData.number_of_players, 10);
      } else {
        delete updateData.number_of_players;
      }
      
      // Convert graduation year to integer if provided, or remove if empty
      if (updateData.graduation_year && updateData.graduation_year.toString().trim()) {
        updateData.graduation_year = parseInt(updateData.graduation_year, 10);
      } else {
        delete updateData.graduation_year;
      }
      
      // Convert weight to string with "lbs" suffix
      if (updateData.weight) {
        updateData.weight = `${updateData.weight} lbs`;
      }
      
      // Combine height fields into string format
      if (updateData.height_feet && updateData.height_inches) {
        updateData.height = `${updateData.height_feet}'${updateData.height_inches}"`;
      } else if (updateData.height_feet) {
        updateData.height = `${updateData.height_feet}'0"`;
      }
      
      // Remove the separate height fields
      delete updateData.height_feet;
      delete updateData.height_inches;
      
      await axios.put(`${API}/auth/profile`, updateData);
      toast.success('Profile updated successfully!');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditingData({});
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG or JPG.');
      return;
    }

    // Validate file size (5MB for cropping - will be compressed after)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit.');
      return;
    }

    // Create preview and open cropper
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    
    // Reset the input so same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = (croppedFile) => {
    setProfileImageFile(croppedFile);
    
    // Create preview from cropped file
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(croppedFile);
    
    setShowCropper(false);
    setImageToCrop(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  const uploadProfileImage = async () => {
    if (!profileImageFile) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', profileImageFile);

      await axios.post(`${API}/user/profile-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Profile image uploaded successfully!');
      setProfileImageFile(null);
      setProfileImagePreview(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile image');
    } finally {
      setUploadingImage(false);
    }
  };

  const deleteProfileImage = async () => {
    if (!user.profile_image) return;
    
    try {
      await axios.delete(`${API}/user/profile-image`);
      toast.success('Profile image deleted successfully!');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to delete profile image');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleDeleteAccount = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/auth/account`);
      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleClose = () => {
    setEditing(false);
    setShowChangePassword(false);
    setProfileImageFile(null);
    setProfileImagePreview(null);
    setShowCropper(false);
    setImageToCrop(null);
    onOpenChange(false);
  };

  // Compute full name from first_name and last_name, with fallback
  const userFullName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}`.trim()
    : user?.full_name || user?.username || 'User';

  return (
    <>
      {/* Delete Account Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Account?"
        description="Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed."
        confirmText="Delete Account"
        cancelText="Cancel"
        onConfirm={confirmDeleteAccount}
        variant="destructive"
      />
      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          circularCrop={false}
        />
      )}
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl max-h-[90vh] bg-blue-950 border border-blue-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-900 border-b border-blue-700 p-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">
              {user?.role === 'coach' ? 'Coach' : 'Athlete'} Profile
            </h2>
            <div className="flex items-center space-x-2">
              {!editing && (
                <Button 
                  onClick={handleEdit} 
                  variant="outline" 
                  size="sm"
                  data-testid="edit-profile-btn"
                  className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-blue-900"
                >
                  Edit Profile
                </Button>
              )}
              <Button 
                onClick={handleClose}
                variant="ghost" 
                size="sm"
                className="text-sky-400 hover:text-white hover:bg-blue-800"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 space-y-4">
          {!editing ? (
            <>
              <div className="flex items-start space-x-4">
                {/* Profile Image Section */}
                <div className="flex flex-col items-center space-y-2">
                  <Avatar className="w-24 h-24 rounded-lg">
                    {user?.profile_image ? (
                      <AvatarImage 
                        src={`${API}/user/${user.id}/profile-image?t=${Date.now()}`}
                        alt={userFullName}
                        className="rounded-lg"
                      />
                    ) : null}
                    <AvatarFallback className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-blue-900 text-3xl font-bold rounded-lg">
                      {userFullName?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Upload/Delete Controls */}
                  <div className="flex flex-col space-y-1">
                    <input
                      type="file"
                      id="profile-image-upload-modal"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleProfileImageChange}
                      className="hidden"
                    />
                    <label htmlFor="profile-image-upload-modal">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="w-full border-sky-400 text-sky-300 hover:bg-sky-900 text-xs"
                        onClick={() => document.getElementById('profile-image-upload-modal').click()}
                      >
                        {user?.profile_image ? 'Change' : 'Upload'}
                      </Button>
                    </label>
                    {user?.profile_image && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={deleteProfileImage}
                        className="w-full border-red-500 text-red-400 hover:bg-red-600 hover:text-white text-xs"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* User Info */}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white">{userFullName}</h3>
                  <p className="text-yellow-400 capitalize text-lg">{user?.role}</p>
                  <p className="text-sm text-sky-300">@{user?.username}</p>
                  <p className="text-sm text-gray-400 mt-1">{user?.email}</p>
                </div>
              </div>
              
              {/* Preview Upload Section */}
              {profileImagePreview && (
                <div className="p-4 bg-blue-900/50 rounded-lg border border-blue-700 space-y-3">
                  <div className="flex items-center space-x-4">
                    <img 
                      src={profileImagePreview} 
                      alt="Preview" 
                      className="w-16 h-16 rounded-lg object-cover border-2 border-yellow-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-white font-medium">New Profile Image</p>
                      <p className="text-xs text-gray-400">{profileImageFile?.name}</p>
                      <p className="text-xs text-gray-400">
                        {(profileImageFile?.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={uploadProfileImage}
                      disabled={uploadingImage}
                      className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
                    >
                      {uploadingImage ? 'Uploading...' : 'Save Image'}
                    </Button>
                    <Button
                      onClick={() => {
                        setProfileImageFile(null);
                        setProfileImagePreview(null);
                      }}
                      variant="outline"
                      className="border-gray-500 text-gray-300 hover:bg-gray-600"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              <Separator className="bg-blue-700" />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs uppercase">Age</Label>
                  <p className="text-white">{user?.age || 'Not specified'}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase">Height</Label>
                  <p className="text-white">{user?.height || 'Not specified'}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase">Weight</Label>
                  <p className="text-white">{user?.weight || 'Not specified'}</p>
                </div>
                {user?.role === 'athlete' && (
                  <>
                    <div>
                      <Label className="text-slate-400 text-xs uppercase">Position</Label>
                      <p className="text-white">{user?.primary_position || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs uppercase">Experience</Label>
                      <p className="text-white capitalize">{user?.experience_level || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs uppercase">Grad Year</Label>
                      <p className="text-white" data-testid="profile-graduation-year">
                        {user?.graduation_year || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs uppercase">Sport Focus</Label>
                      <p className="text-white">{user?.focused_sport || 'Not specified'}</p>
                    </div>
                  </>
                )}
                {user?.role === 'coach' && (
                  <>
                    <div>
                      <Label className="text-slate-400 text-xs uppercase">Organization</Label>
                      <p className="text-white">{user?.organization_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs uppercase">Coaching Experience</Label>
                      <p className="text-white">{user?.coaching_experience || 'Not specified'}</p>
                    </div>
                  </>
                )}
              </div>
              
              <Separator className="bg-blue-700" />
              
              {/* Account Management Section */}
              <div className="space-y-3">
                <h4 className="text-white font-semibold">Account Management</h4>
                
                {/* Change Password Section */}
                {!showChangePassword ? (
                  <Button
                    onClick={() => setShowChangePassword(true)}
                    variant="outline"
                    className="w-full border-blue-400 text-blue-400 hover:bg-blue-800"
                  >
                    Change Password
                  </Button>
                ) : (
                  <div className="space-y-3 p-4 bg-blue-900/50 rounded-lg border border-blue-700">
                    <div className="space-y-2">
                      <Label htmlFor="current_password_modal" className="text-white">Current Password</Label>
                      <Input
                        id="current_password_modal"
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new_password_modal" className="text-white">New Password</Label>
                      <Input
                        id="new_password_modal"
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm_password_modal" className="text-white">Confirm New Password</Label>
                      <Input
                        id="confirm_password_modal"
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white"
                      />
                    </div>
                    <p className="text-xs text-yellow-400">
                      Password must be at least 8 characters with uppercase, lowercase, number, and special character
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        onClick={handleChangePassword}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-blue-900"
                      >
                        Save Password
                      </Button>
                      <Button
                        onClick={() => {
                          setShowChangePassword(false);
                          setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                        }}
                        variant="outline"
                        className="flex-1 border-gray-500 text-gray-300 hover:bg-gray-600"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Delete Account Button */}
                <Button
                  onClick={handleDeleteAccount}
                  variant="outline"
                  className="w-full border-red-500 text-red-400 hover:bg-red-600 hover:text-white"
                >
                  Delete Account
                </Button>
                <p className="text-xs text-gray-400">
                  Warning: Deleting your account will permanently remove all your data and cannot be undone.
                </p>
              </div>
              
              {/* Team Management Section - Athletes Only */}
              {user?.role === 'athlete' && (
                <>
                  <Separator className="bg-blue-700 my-6" />
                  <div className="space-y-6">
                    <MyTeamsCard />
                    <TeamJoinRequestCard />
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_first_name_modal" className="text-white">First Name</Label>
                  <Input
                    id="edit_first_name_modal"
                    value={editingData.first_name}
                    onChange={(e) => setEditingData({...editingData, first_name: e.target.value})}
                    className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_last_name_modal" className="text-white">Last Name</Label>
                  <Input
                    id="edit_last_name_modal"
                    value={editingData.last_name}
                    onChange={(e) => setEditingData({...editingData, last_name: e.target.value})}
                    className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_email_modal" className="text-white">Email</Label>
                  <Input
                    id="edit_email_modal"
                    type="email"
                    value={editingData.email}
                    onChange={(e) => setEditingData({...editingData, email: e.target.value})}
                    className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_age_modal" className="text-white">Age</Label>
                  <Input
                    id="edit_age_modal"
                    type="number"
                    value={editingData.age}
                    onChange={(e) => setEditingData({...editingData, age: parseInt(e.target.value) || ''})}
                    className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Height</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Input
                        type="number"
                        min="3"
                        max="8"
                        value={editingData.height_feet}
                        onChange={(e) => setEditingData({...editingData, height_feet: e.target.value})}
                        placeholder="5"
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                      />
                      <Label className="text-xs text-yellow-400">Feet</Label>
                    </div>
                    <div className="space-y-1">
                      <Input
                        type="number"
                        min="0"
                        max="11"
                        value={editingData.height_inches}
                        onChange={(e) => setEditingData({...editingData, height_inches: e.target.value})}
                        placeholder="10"
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                      />
                      <Label className="text-xs text-yellow-400">Inches</Label>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_weight_modal" className="text-white">Weight (lbs)</Label>
                  <Input
                    id="edit_weight_modal"
                    type="number"
                    min="50"
                    max="500"
                    value={editingData.weight}
                    onChange={(e) => setEditingData({...editingData, weight: e.target.value})}
                    placeholder="150"
                    className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  />
                </div>
                {user?.role === 'athlete' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="edit_graduation_year_modal" className="text-white">Graduation Year</Label>
                      <Input
                        id="edit_graduation_year_modal"
                        type="number"
                        min="2024"
                        max="2040"
                        placeholder="2027"
                        value={editingData.graduation_year}
                        onChange={(e) => setEditingData({...editingData, graduation_year: e.target.value})}
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                        data-testid="edit-graduation-year-input"
                      />
                      <p className="text-xs text-yellow-400/80">Shown on your recruiter stat card</p>
                    </div>
                    <div className="flex items-start justify-between gap-4 rounded-lg border border-blue-700 bg-blue-900/30 p-3">
                      <div className="space-y-1">
                        <Label htmlFor="edit_share_contact_modal" className="text-white">
                          Show my email on my stat card
                        </Label>
                        <p className="text-xs text-gray-400">
                          Off by default. Turn on only if you want recruiters to be able to contact you directly.
                        </p>
                      </div>
                      <Switch
                        id="edit_share_contact_modal"
                        checked={!!editingData.share_contact_info}
                        onCheckedChange={(checked) => setEditingData({...editingData, share_contact_info: checked})}
                        data-testid="edit-share-contact-switch"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_position_modal" className="text-white">Primary Position</Label>
                      <Input
                        id="edit_position_modal"
                        value={editingData.primary_position}
                        onChange={(e) => setEditingData({...editingData, primary_position: e.target.value})}
                        className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_experience_modal" className="text-white">Experience Level</Label>
                      <select
                        id="edit_experience_modal"
                        value={editingData.experience_level}
                        onChange={(e) => setEditingData({...editingData, experience_level: e.target.value})}
                        className="w-full px-3 py-2 border border-blue-700 rounded-md focus:border-yellow-500 focus:outline-none bg-blue-900/50 text-white"
                      >
                        <option value="" className="bg-blue-900 text-white">Select level</option>
                        <option value="beginner" className="bg-blue-900 text-white">Beginner</option>
                        <option value="intermediate" className="bg-blue-900 text-white">Intermediate</option>
                        <option value="advanced" className="bg-blue-900 text-white">Advanced</option>
                        <option value="elite" className="bg-blue-900 text-white">Elite</option>
                        <option value="professional" className="bg-blue-900 text-white">Professional</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  onClick={handleSave} 
                  disabled={loading} 
                  data-testid="save-profile-btn"
                  className="bg-gradient-to-r from-blue-900 to-yellow-500 hover:from-blue-950 hover:to-yellow-600 text-white"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  onClick={handleCancel} 
                  variant="outline"
                  className="border-sky-400 text-sky-300 hover:bg-blue-800 hover:text-white"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ProfileModal;
